import { useState, useEffect, useRef } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { firestore } from '../../firebaseConfig';

/**
 * useSentRecipients — tells the composer which recipient addresses we have
 * ALREADY sent an outbound mail to (intro or any follow-up), so the To-field
 * can flag them red and the send flow can demand an extra confirmation before
 * mailing the very same address twice.
 *
 * Reuses the exact same source of truth as EmailHistoryPanel
 * (`world_sent/{userId}/emails`):
 *   • by `leadId` → collect the `to` address of every mail sent to this lead
 *   • by recipient address → covers decision-maker mails not linked via leadId
 *
 * Crucially this is PER-ADDRESS: a brand-new shareholder/DM email on the same
 * domain that we have not contacted yet stays un-flagged and fully sendable.
 * Only the specific addresses we already mailed are blocked behind a confirm.
 */

const normalizeAddr = (v) => (v == null ? '' : String(v)).trim().toLowerCase();

// `to` is usually a plain lowercased address, but defensively support arrays.
const collectToAddresses = (value, sink) => {
  if (!value) return;
  if (Array.isArray(value)) {
    value.forEach((v) => {
      const a = normalizeAddr(v);
      if (a) sink.add(a);
    });
    return;
  }
  const a = normalizeAddr(value);
  if (a) sink.add(a);
};

const toMillis = (value) => {
  if (value == null) return 0;
  if (typeof value === 'object' && typeof value.toMillis === 'function') return value.toMillis();
  const ms = typeof value === 'number' ? value : new Date(value).getTime();
  return Number.isFinite(ms) ? ms : 0;
};

// Kompakter HTML→Text-Stripper für Preview-Zwecke. Wir zeigen keine HTML im
// Confirm-Modal, weil dort a) kein Sanitizer benötigt sein sollte und b) das
// Layout kompakt bleiben muss. Reihenfolge der Ersetzungen ist wichtig:
// zuerst <style>/<script> raus, dann Block-Elemente zu Newlines, dann Tags weg.
const stripHtmlToText = (raw) => {
  if (!raw || typeof raw !== 'string') return '';
  return raw
    .replace(/<style[\s\S]*?<\/style>/gi, '')
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<(br|BR)\s*\/?>(?!\n)/g, '\n')
    .replace(/<\/(p|div|h[1-6]|li|tr)>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

export default function useSentRecipients(userId, contactId, recipientEmails = []) {
  const [contactedAddresses, setContactedAddresses] = useState(() => new Set());
  const [sentCount, setSentCount] = useState(0);
  const [lastSent, setLastSent] = useState(null);
  const [sentMessages, setSentMessages] = useState([]);
  // Full newest-first list, including stage + template, so a follow-up can
  // tell "this step already went out" from "only the intro went out".
  const [priorSends, setPriorSends] = useState([]);
  const [loading, setLoading] = useState(false);

  // Stable key so the effect doesn't re-run on every render.
  const addrKey = (recipientEmails || [])
    .map(normalizeAddr)
    .filter(Boolean)
    .sort()
    .join(',');

  const reqRef = useRef(0);

  // Drop the previous contact's history in the same render the recipient
  // changes. Otherwise a fast Send & Next in bulk mode confirms against the
  // contact that was just left.
  const scopeKey = `${userId || ''}|${contactId || ''}|${addrKey}`;
  const [historyScope, setHistoryScope] = useState(scopeKey);
  if (scopeKey !== historyScope) {
    setHistoryScope(scopeKey);
    setContactedAddresses(new Set());
    setSentCount(0);
    setLastSent(null);
    setSentMessages([]);
    setPriorSends([]);
    setLoading(Boolean(userId && (contactId || addrKey)));
  }

  useEffect(() => {
    if (!userId || (!contactId && !addrKey)) {
      setContactedAddresses(new Set());
      setSentCount(0);
      setLastSent(null);
      setSentMessages([]);
      setPriorSends([]);
      setLoading(false);
      return;
    }

    const reqId = ++reqRef.current;
    setLoading(true);
    const addresses = addrKey ? addrKey.split(',') : [];

    (async () => {
      const sent = new Set();
      const seen = new Set();
      const collected = [];
      const pushDoc = (d) => {
        if (seen.has(d.id)) return;
        seen.add(d.id);
        const data = d.data();
        collectToAddresses(data.to, sent);
        // Text-Body bevorzugt (kürzer, kein Markup) — mit Fallback auf
        // HTML→Text-Konversion, damit ältere Docs ohne `text` trotzdem
        // eine lesbare Preview liefern.
        const bodyText = (data.text && String(data.text).trim())
          || stripHtmlToText(data.content || data.body || '')
          || '';
        collected.push({
          id: d.id,
          subject: data.subject || '',
          ms: toMillis(data.timestamp || data.sentAt || data.date || data.createdAt),
          bodyText,
          from: data.from || '',
          to: Array.isArray(data.to) ? data.to[0] : (data.to || ''),
          emailType: data.emailType || '',
          templateId: data.templateId || '',
          templateName: data.templateName || data.topicName || '',
        });
      };

      try {
        const sentRef = collection(firestore, `world_sent/${userId}/emails`);

        if (contactId) {
          try {
            const snap = await getDocs(query(sentRef, where('leadId', '==', contactId)));
            snap.docs.forEach(pushDoc);
          } catch (_) { /* fall through to address match */ }
        }

        for (const addr of addresses) {
          try {
            const snap = await getDocs(query(sentRef, where('to', '==', addr)));
            // address match also implies the address itself was contacted
            if (!snap.empty) sent.add(addr);
            snap.docs.forEach(pushDoc);
          } catch (_) { /* ignore single-address failures */ }
        }
      } catch (err) {
        console.warn('[useSentRecipients] load failed:', err?.message);
      }

      if (reqId !== reqRef.current) return;
      collected.sort((a, b) => b.ms - a.ms);
      setContactedAddresses(sent);
      setSentCount(collected.length);
      setLastSent(collected.length > 0 ? collected[0] : null);
      // Nur die letzten 10 Nachrichten für die Preview — mehr wäre in einer
      // Confirm-Modal unübersichtlich und der User will primär die letzten
      // Steps sehen (Intro + 2-3 Follow-ups).
      setSentMessages(collected.slice(0, 10));
      setPriorSends(collected);
      setLoading(false);
    })();
  }, [userId, contactId, addrKey]);

  const isContacted = (email) => contactedAddresses.has(normalizeAddr(email));

  return { contactedAddresses, isContacted, sentCount, lastSent, sentMessages, priorSends, loading };
}
